import { useState, type FormEvent } from "react";
import { useApi } from "../useApi.js";
import { api, dateDe } from "../api.js";
import type { CrewRow } from "../types.js";
import { Page } from "../components/Shell.js";
import { TableSkeleton } from "../components/Skeleton.js";
import ConfirmDeleteButton from "../components/ConfirmDeleteButton.js";
import { toast } from "../toast.js";

/** Standort einer Kolonne inline bearbeiten – Speichern/Abbrechen wie bei den Preisen. */
function LocationCell({ location, onSave }: {
  location: string; onSave: (location: string) => Promise<void>;
}) {
  const [value, setValue] = useState(location);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dirty = value.trim().length > 0 && value !== location;

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await onSave(value.trim());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Speichern fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
        <input value={value} onChange={(e) => setValue(e.target.value)}
               style={{ width: 160, padding: "6px 9px", fontSize: 13, background: "var(--surface)",
                        border: "1px solid var(--line-2)", borderRadius: "var(--r-sm)" }} />
        {dirty && (
          <span style={{ display: "inline-flex", gap: 6 }}>
            <button className="btn ghost sm" disabled={busy} onClick={() => { setValue(location); setErr(null); }}>
              Abbrechen
            </button>
            <button className="btn pri sm" disabled={busy} onClick={() => void save()}>
              {busy ? "Speichert…" : "Speichern"}
            </button>
          </span>
        )}
      </div>
      {err && <div className="err" style={{ marginTop: 8, marginBottom: 0 }}>{err}</div>}
    </div>
  );
}

export default function Crews() {
  const { data, error, loading, reload } = useApi<CrewRow[]>("/api/crews");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      await api.post("/api/crews", { name, location });
      setName(""); setLocation(""); setOpen(false);
      await reload();
      toast("Kolonne angelegt", "success");
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Anlegen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  }

  async function saveLocation(crewName: string, newLocation: string) {
    await api.patch(`/api/crews/${encodeURIComponent(crewName)}`, { location: newLocation });
    await reload();
    toast("Standort gespeichert", "success");
  }

  async function remove(crewName: string) {
    setRowError(null);
    try {
      await api.delete(`/api/crews/${encodeURIComponent(crewName)}`);
      await reload();
      toast("Kolonne gelöscht", "success");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Löschen fehlgeschlagen";
      setRowError(message);
      toast(message, "error");
    }
  }

  return (
    <Page title="Kolonnen" sub={data ? `${data.length} Kolonnen` : undefined}
          actions={<button className="btn pri" onClick={() => setOpen((o) => !o)}>
            {open ? "Abbrechen" : "Kolonne anlegen"}
          </button>}>

      {open && (
        <div className="card" style={{ marginBottom: 16, maxWidth: 440 }}>
          <div className="card-h"><h3>Neue Kolonne</h3></div>
          <form className="card-b" onSubmit={submit}>
            {formError && <div className="err" role="alert">{formError}</div>}
            <div className="field">
              <label htmlFor="c-name">Name</label>
              <input id="c-name" required placeholder="z. B. Kolonne 4 – Süd"
                     value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="c-loc">Standort</label>
              <input id="c-loc" required placeholder="z. B. Fulda"
                     value={location} onChange={(e) => setLocation(e.target.value)} />
            </div>
            <button className="btn pri" type="submit" disabled={busy}
                    style={{ width: "100%", justifyContent: "center", marginTop: 6 }}>
              {busy ? "Wird angelegt…" : "Anlegen"}
            </button>
          </form>
        </div>
      )}

      <div className="card">
        <div className="card-h">
          <h3>Alle Kolonnen</h3>
          <span className="hint">Geräte wählen die Kolonne aus dieser Liste, kein Freitext mehr</span>
        </div>
        {rowError && <div className="err" style={{ margin: "0 16px" }}>{rowError}</div>}
        {loading && <TableSkeleton widths={[30, 25, 20]} />}
        {error && <div className="empty">{error}</div>}
        {data && data.length === 0 && <div className="empty">Noch keine Kolonne angelegt.</div>}
        {data && data.length > 0 && (
          <div className="tbl-wrap"><table>
            <thead><tr><th>Name</th><th>Standort</th><th>Angelegt</th><th /></tr></thead>
            <tbody>
              {data.map((c) => (
                <tr key={c.name}>
                  <td><b>{c.name}</b></td>
                  <td><LocationCell location={c.location}
                                    onSave={(loc) => saveLocation(c.name, loc)} /></td>
                  <td className="num">{dateDe(c.createdAt)}</td>
                  <td className="r">
                    <ConfirmDeleteButton onConfirm={() => remove(c.name)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </Page>
  );
}
